import { useState, useRef } from "react";
import { Download, Upload, FileText, Database, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { TranslationEntry } from "@/hooks/useTranslationHistory";
import { FeatureTooltip, TOOLTIP_CONTENT } from "./FeaturesTooltips";

interface ImportExportActionsProps {
  entries: TranslationEntry[];
  onExportHistory: (format: 'json' | 'csv') => string;
  onImportHistory: (data: string, format: 'json' | 'csv') => {
    success: boolean;
    imported: number;
    errors: string[];
  };
}

export function ImportExportActions({
  entries,
  onExportHistory,
  onImportHistory,
}: ImportExportActionsProps) {
  const { toast } = useToast();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    imported: number;
    errors: string[];
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [importFormat, setImportFormat] = useState<'json' | 'csv'>('json');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = (format: 'json' | 'csv') => {
    try {
      const data = onExportHistory(format);
      const blob = new Blob([data], { 
        type: format === 'json' ? 'application/json' : 'text/csv' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      
      link.href = url;
      link.download = `translateloo-history-${timestamp}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Export successful",
        description: `Downloaded ${entries.length} translation${entries.length !== 1 ? 's' : ''} as ${format.toUpperCase()}`,
        duration: 3000,
      });

      setExportDialogOpen(false);
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Could not export translation history",
        variant: "destructive",
      });
    }
  };

  const parseFileContent = (content: string, format: 'json' | 'csv') => {
    try {
      if (format === 'json') {
        const parsed = JSON.parse(content);
        return Array.isArray(parsed) ? parsed : [parsed];
      } else {
        // Basic CSV parsing for preview
        const lines = content.split('\n').filter(line => line.trim());
        if (lines.length === 0) return [];
        
        const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, ''));
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.replace(/^"|"$/g, '').replace(/""/g, '"'));
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = values[index] || '';
          });
          return obj;
        });
        return data;
      }
    } catch (error) {
      return [];
    }
  };

  const handleFileSelect = (file: File) => {
    if (!file) return;

    const format = file.name.endsWith('.csv') ? 'csv' : 'json';
    setImportFormat(format);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const preview = parseFileContent(content, format);
      setPreviewData(preview.slice(0, 5)); // Show first 5 entries for preview
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const file = files.find(f => f.name.endsWith('.json') || f.name.endsWith('.csv'));
    
    if (file) {
      handleFileSelect(file);
    } else {
      toast({
        title: "Invalid file type",
        description: "Please select a JSON or CSV file",
        variant: "destructive",
      });
    }
  };

  const handleImport = () => {
    if (!fileInputRef.current?.files?.[0]) {
      toast({
        title: "No file selected",
        description: "Please select a file to import",
        variant: "destructive",
      });
      return;
    }

    const file = fileInputRef.current.files[0];
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const content = e.target?.result as string;
      
      // Simulate progress
      setImportProgress(30);
      
      setTimeout(() => {
        setImportProgress(70);
        
        setTimeout(() => {
          const result = onImportHistory(content, importFormat);
          setImportResult(result);
          setImportProgress(100);
          
          if (result.success) {
            toast({
              title: "Import successful",
              description: `Imported ${result.imported} translation${result.imported !== 1 ? 's' : ''}`,
              duration: 3000,
            });
          } else {
            toast({
              title: "Import completed with errors",
              description: `${result.errors.length} error${result.errors.length !== 1 ? 's' : ''} occurred`,
              variant: "destructive",
            });
          }
        }, 500);
      }, 500);
    };
    
    reader.readAsText(file);
  };

  const resetImport = () => {
    setImportResult(null);
    setImportProgress(0);
    setPreviewData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogTrigger asChild>
          <FeatureTooltip content={TOOLTIP_CONTENT.EXPORT}>
            <Button variant="outline" size="sm" className="text-xs" aria-label="Export translation history">
              <Download className="h-3 w-3 mr-1" />
              Export
            </Button>
          </FeatureTooltip>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Translation History
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Export {entries.length} translation{entries.length !== 1 ? 's' : ''} including text, languages, timestamps, categories, and tags.
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handleExport('json')}
                className="flex-col h-auto p-4 space-y-2"
                variant="outline"
              >
                <Database className="h-6 w-6" />
                <div className="text-xs font-medium">JSON Format</div>
                <div className="text-xs text-muted-foreground">
                  Best for re-importing
                </div>
              </Button>
              
              <Button
                onClick={() => handleExport('csv')}
                className="flex-col h-auto p-4 space-y-2"
                variant="outline"
              >
                <FileText className="h-6 w-6" />
                <div className="text-xs font-medium">CSV Format</div>
                <div className="text-xs text-muted-foreground">
                  For spreadsheets
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) resetImport();
      }}>
        <DialogTrigger asChild>
          <FeatureTooltip content={TOOLTIP_CONTENT.IMPORT}>
            <Button variant="outline" size="sm" className="text-xs" aria-label="Import translation history">
              <Upload className="h-3 w-3 mr-1" />
              Import
            </Button>
          </FeatureTooltip>
        </DialogTrigger>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Import Translation History
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="upload" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload">Upload File</TabsTrigger>
              <TabsTrigger value="preview" disabled={previewData.length === 0}>
                Preview ({previewData.length > 0 ? previewData.length : 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="space-y-4">
              {/* File Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
              >
                <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm font-medium">
                  Drop your translation history file here
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports JSON and CSV formats
                </p>
                
                <div className="mt-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,.csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileSelect(file);
                    }}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs"
                  >
                    Browse Files
                  </Button>
                </div>
              </div>

              {/* Import Progress */}
              {importProgress > 0 && importProgress < 100 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Importing...</span>
                    <span>{importProgress}%</span>
                  </div>
                  <Progress value={importProgress} />
                </div>
              )}

              {/* Import Result */}
              {importResult && (
                <Alert className={importResult.success ? "border-green-200" : "border-destructive"}>
                  <div className="flex items-start gap-2">
                    {importResult.success ? (
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                    )}
                    <div className="flex-1">
                      <AlertDescription>
                        {importResult.success ? (
                          <div>
                            <strong>Import successful!</strong>
                            <br />
                            Imported {importResult.imported} translation{importResult.imported !== 1 ? 's' : ''}
                          </div>
                        ) : (
                          <div>
                            <strong>Import completed with errors</strong>
                            <br />
                            {importResult.errors.slice(0, 3).map((error, index) => (
                              <div key={index} className="text-xs mt-1">• {error}</div>
                            ))}
                            {importResult.errors.length > 3 && (
                              <div className="text-xs mt-1">
                                ... and {importResult.errors.length - 3} more errors
                              </div>
                            )}
                          </div>
                        )}
                      </AlertDescription>
                    </div>
                  </div>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="preview" className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Preview of data to be imported (showing first 5 entries):
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {previewData.map((entry, index) => (
                  <div key={index} className="p-3 bg-muted rounded-lg space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {entry.sourceLanguage || entry['Source Language'] || 'Unknown'} → {entry.targetLanguage || entry['Target Language'] || 'Unknown'}
                      </Badge>
                      {(entry.isFavorite || entry.Favorite === 'Yes') && (
                        <Badge variant="secondary" className="text-xs">⭐ Favorite</Badge>
                      )}
                    </div>
                    <div className="text-sm">
                      <div className="text-muted-foreground">
                        {entry.sourceText || entry['Source Text'] || 'No source text'}
                      </div>
                      <div className="font-medium">
                        {entry.translatedText || entry['Translated Text'] || 'No translation'}
                      </div>
                    </div>
                    {(entry.category || entry.Category) && (
                      <Badge variant="outline" className="text-xs">
                        📁 {entry.category || entry.Category}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {previewData.length > 0 && !importResult && (
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleImport} className="flex-1">
                Import {previewData.length > 5 ? 'All' : previewData.length} Translation{previewData.length !== 1 ? 's' : ''}
              </Button>
              <Button variant="outline" onClick={resetImport}>
                Cancel
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}